import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Crown } from "lucide-react";
import { api, apiErrorMessage } from "@/lib/api";

const PLAN_LABELS = {
  membership_foundation_monthly: "Foundation",
  membership_unlimited_monthly: "Unlimited",
  membership_unlimited_yearly: "Unlimited Annual",
};

export default function AdminFoundingMembers() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [togglingId, setTogglingId] = useState(null);

  const fetchMembers = () => {
    setLoading(true);
    api
      .get("/admin/founding-members")
      .then(({ data }) => setMembers(data))
      .catch((err) => toast.error(apiErrorMessage(err)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchMembers();
  }, []);

  const handleToggleKit = async (id, current) => {
    setTogglingId(id);
    try {
      await api.patch(`/admin/founding-members/${id}/kit-sent`, { kit_sent: !current });
      setMembers((prev) => prev.map((m) => (m.id === id ? { ...m, kit_sent: !current } : m)));
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setTogglingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-8 w-8 text-gold animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="admin-founding-members-page">
      <div className="flex items-center gap-2 mb-2">
        <Crown className="h-5 w-5 text-gold" />
        <h2 className="font-anton uppercase text-cream text-xl">Founding 50 Members</h2>
      </div>
      <p className="text-smoke text-sm mb-6">
        Members who claimed the Founding 50 offer — tick off once their free kit bundle has shipped.
      </p>

      {members.length === 0 ? (
        <p className="text-smoke" data-testid="founding-members-empty">
          No founding members yet — spots are still being claimed on the Membership page.
        </p>
      ) : (
        <div className="border border-line divide-y divide-line" data-testid="founding-members-list">
          {members.map((m) => (
            <div
              key={m.id}
              className="bg-surface p-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6"
              data-testid={`founding-member-row-${m.id}`}
            >
              <div className="flex-1">
                <p className="text-cream font-semibold">{m.name}</p>
                <p className="text-smoke text-sm">{m.email}</p>
              </div>
              <span className="text-xs uppercase tracking-widest px-3 py-1 border border-gold text-gold w-fit">
                {PLAN_LABELS[m.membership_plan] || m.membership_plan}
              </span>
              <label className="flex items-center gap-2 text-sm text-smoke cursor-pointer w-fit" data-testid={`kit-sent-toggle-${m.id}`}>
                <input
                  type="checkbox"
                  checked={m.kit_sent}
                  disabled={togglingId === m.id}
                  onChange={() => handleToggleKit(m.id, m.kit_sent)}
                  className="h-4 w-4 accent-gold"
                />
                Kit bundle sent
              </label>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
